import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Nullable } from '@common/type/CommonTypes';
import { Box, BoxProps, Button, Chip, Typography, useTheme } from '@mui/material';
import { noop } from 'lodash';
import { useTranslation } from 'next-i18next';

import { ContentStyle, DiscussionItemStyle } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { NextImage } from '@components/atoms/image';
import dateUtil from '@helpers/dateUtils';
import { getAssetUrl } from '@helpers/utils';

export enum CourseDiscussionItemType {
  LIST_ITEM = 'list-item',
  FULL_INFO = 'full-info',
  REPLY = 'reply',
}

const MAX_LINE_CLAMP = 3;

type CourseDiscussionItemProps = {
  type: CourseDiscussionItemType;
  imageUrl: string;
  fullName: string;
  content: string;
  createdAt: Date;
  isAuthor?: boolean;
  replyCount?: number;
  onClickViewInfo?: typeof noop;
  sx?: BoxProps['sx'];
};

const CourseDiscussionItem: FC<CourseDiscussionItemProps> = ({
  type,
  imageUrl,
  fullName,
  content,
  createdAt,
  isAuthor = false,
  replyCount = 0,
  onClickViewInfo,
  sx = {},
}) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const contentRef = useRef<Nullable<HTMLDivElement>>(null);

  const [isShowAllContent, setIsShowAllContent] = useState<boolean>(false);
  const [lineCount, setLineCount] = useState<number>(0);

  const isListItemType = type === CourseDiscussionItemType.LIST_ITEM;
  const isReplyMore = type === CourseDiscussionItemType.REPLY && lineCount > MAX_LINE_CLAMP;

  const userImageSize = useMemo(() => {
    return type === CourseDiscussionItemType.REPLY ? 30 : 40;
  }, [type]);

  const getCreatedAt = () => {
    const currentTime = dateUtil().utc();
    // prevent time on server and client overlap
    return currentTime.isSameOrBefore(dateUtil(createdAt)) ? currentTime : createdAt;
  };

  useEffect(() => {
    if (contentRef.current && type !== CourseDiscussionItemType.FULL_INFO) {
      const HEIGHT_OF_LINE = 24;
      setLineCount(Math.ceil(contentRef.current.offsetHeight / HEIGHT_OF_LINE));
    }
  }, [contentRef, type]);

  return (
    <DiscussionItemStyle type={type} sx={{ ...sx }}>
      <Box width={userImageSize} minWidth={userImageSize} height={userImageSize} borderRadius="100%" overflow="hidden">
        <NextImage
          src={imageUrl}
          alt={fullName}
          layout="responsive"
          width={userImageSize}
          height={userImageSize}
          errorImage={getAssetUrl('thumbnail-instructor.jpg')}
        />
      </Box>

      <Box sx={{ background: theme.palette.grey[100], padding: '8px 16px 8px 16px', borderRadius: '9px', flex: '1' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box display="grid" gridTemplateColumns="auto 1fr" gap={1}>
            <Typography
              variant="h5"
              color={theme.palette.textV2.primary}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fullName}
            </Typography>
            {isAuthor && (
              <Chip className="badge-author" label={t('course_detail.course_discussion.instructor_badge')} />
            )}
          </Box>
          <Typography variant="h11" color={theme.palette.textV2.disabled} sx={{ lineHeight: '14px' }}>
            {dateUtil(getCreatedAt()).fromNow()}
          </Typography>
        </Box>
        <ContentStyle type={type} isShowAll={isShowAllContent} lineClamp={String(MAX_LINE_CLAMP)} variant="body1lanna">
          <Typography ref={contentRef} component="span" variant="body1lanna" color={theme.palette.textV2.primary}>
            {content}
          </Typography>
        </ContentStyle>

        {isListItemType ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {replyCount > 0 && (
                <Typography variant="BaseLegacy" color={theme.palette.textV2.disabled}>
                  {t('course_detail.course_discussion.reply_modal.reply_list', { replyCount })}
                </Typography>
              )}
            </Box>
            <Button onClick={onClickViewInfo} variant="text" sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="button_deprecated">{t('course_detail.course_discussion.see_detail')}</Typography>
              <FontAwesomeIcon icon="fa-light fa-arrow-right" width={16} color={theme.palette.primaryV2.main} />
            </Button>
          </Box>
        ) : null}

        {isReplyMore ? (
          <Box py={1}>
            <Button
              onClick={() => setIsShowAllContent(!isShowAllContent)}
              variant="text"
              sx={{ color: theme.palette.primary.main, minWidth: 48, height: 22, p: 0 }}
              disableRipple
            >
              {isShowAllContent ? t('course_detail.course_review.see_less') : t('course_detail.course_review.see_more')}
            </Button>
          </Box>
        ) : null}
      </Box>
    </DiscussionItemStyle>
  );
};

export default CourseDiscussionItem;
